import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDownload, faTruckRampBox } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { PageHeader } from '@/components/shared/PageHeader';
import { FormField } from '@/components/shared/FormField';
import { Button } from '@/components/ui/button';
import { customerService } from '@/services/customer.service';
import { receivingLotService } from '@/services/receiving-lot.service';
import { priorityService } from '@/services/priority.service';
import { bulkImportService, type BulkImportTargetMode } from '@/services/bulk-import.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { CustomerSummary } from '@/features/equipment-intake/components/CustomerSummary';
import { AddressOverview } from '@/features/equipment-intake/components/AddressOverview';
import { AddressPickModal } from '@/features/equipment-intake/components/AddressPickModal';
import { AddContactModal } from '@/features/equipment-intake/CustomerInlineModals';
import {
  applyReceivedFromChange,
  returnFollowing,
} from '@/features/equipment-intake/components/received-from-wiring';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { generateUuid } from '@/lib/uuid';
import { formatCustomerLabel } from '@/lib/customer-label';
import { ROUTES } from '@/routes/routes';

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

const INPUT_CLASS = SELECT_CLASS;

type PickTarget = 'location' | 'contact' | 'return' | 'invoice' | null;

const CUSTOMER_DEFAULT: AddressSelection = { source: 'CUSTOMER_DEFAULT' };

export function BulkImportNewPage() {
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState('');
  const [customerLocationId, setCustomerLocationId] = useState('');
  const [targetMode, setTargetMode] = useState<BulkImportTargetMode>('NEW_LOT');
  const [targetReceivingLotId, setTargetReceivingLotId] = useState('');

  // ── Intake header — stated ONCE for the whole delivery ────────────────────
  const [complaintDescription, setComplaintDescription] = useState('');
  const [customerContactId, setCustomerContactId] = useState('');
  const [billTo, setBillTo] = useState<AddressSelection>(CUSTOMER_DEFAULT);
  const [shipTo, setShipTo] = useState<AddressSelection>(CUSTOMER_DEFAULT);
  const [shipToOverridden, setShipToOverridden] = useState(false);
  const [priorityId, setPriorityId] = useState('');
  const [notes, setNotes] = useState('');

  const [picking, setPicking] = useState<PickTarget>(null);
  const [addContactOpen, setAddContactOpen] = useState(false);

  // Minted once per page load, reused across retry attempts after a failed
  // create — only rotated after a successful create (matches the pattern in
  // AddArrivalDialog.tsx: retries must resend the same key for the server's
  // idempotency guarantee to hold).
  const [idempotencyKey, setIdempotencyKey] = useState(() => generateUuid());

  const { data: customers } = useQuery({
    queryKey: ['customers-mini'],
    queryFn: () => customerService.list({ pageSize: 100 }),
  });

  // Full record — CustomerSummary renders the registered address, which the
  // list projection does not carry.
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customerService.get(customerId),
    enabled: !!customerId,
  });

  const { data: customerLocations } = useQuery({
    queryKey: ['customer-locations', customerId],
    queryFn: () => customerService.listLocations(customerId),
    enabled: !!customerId,
  });

  const { data: customerContacts, refetch: refetchContacts } = useQuery({
    queryKey: ['customer-contacts', customerId],
    queryFn: () => customerService.listContacts(customerId),
    enabled: !!customerId,
  });

  const { data: priorities } = useQuery({
    queryKey: ['priorities-all'],
    queryFn: () => priorityService.listAll(),
  });

  const { data: openLots, isLoading: openLotsLoading } = useQuery({
    queryKey: ['receiving-lots-open', customerId],
    queryFn: () => receivingLotService.list({ customerId, status: 'OPEN', pageSize: 100 }),
    enabled: !!customerId && targetMode === 'EXISTING_LOT',
  });

  const activeLocations = useMemo(
    () => (customerLocations ?? []).filter((l) => !l.deletedAt),
    [customerLocations],
  );
  const activeContacts = useMemo(
    () => (customerContacts ?? []).filter((c) => !c.deletedAt && c.isActive),
    [customerContacts],
  );

  const handleCustomerChange = (id: string) => {
    setCustomerId(id);
    setCustomerLocationId('');
    setTargetReceivingLotId('');
    // A header belongs to one customer. Carrying any of it across would name
    // another customer's branch or contact.
    setCustomerContactId('');
    setBillTo(CUSTOMER_DEFAULT);
    setShipTo(CUSTOMER_DEFAULT);
    setShipToOverridden(false);
  };

  // The locked chain, reused from Single Intake rather than reimplemented:
  // Received From clears the Contact and drags Shipping with it, while Billing
  // never follows — billing is agreed with the customer, not the loading bay.
  const handleLocationChange = (locationId: string) => {
    setCustomerLocationId(locationId);
    const next = applyReceivedFromChange(locationId, {
      returnTo: shipTo,
      returnOverridden: shipToOverridden,
    });
    setCustomerContactId(next.contactId);
    setShipTo(next.returnTo);
    setShipToOverridden(next.returnOverridden);
  };

  const handleShipToChange = (next: AddressSelection) => {
    setShipTo(next);
    setShipToOverridden(
      JSON.stringify(next) !== JSON.stringify(returnFollowing(customerLocationId)),
    );
  };

  const handleModeChange = (mode: BulkImportTargetMode) => {
    setTargetMode(mode);
    setTargetReceivingLotId('');
  };

  const canDownloadTemplate = !!customerId && (targetMode === 'NEW_LOT' || !!targetReceivingLotId);

  const downloadMutation = useMutation({
    mutationFn: () =>
      bulkImportService.downloadTemplate({
        customerId,
        customerLocationId: customerLocationId || undefined,
        targetMode,
        targetReceivingLotId: targetMode === 'EXISTING_LOT' ? targetReceivingLotId : undefined,
      }),
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to download template')),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      bulkImportService.create({
        customerId,
        customerLocationId: customerLocationId || undefined,
        complaintDescription: complaintDescription.trim(),
        customerContactId: customerContactId || undefined,
        billTo,
        shipTo,
        priorityId: priorityId || undefined,
        notes: notes.trim() || undefined,
        targetMode,
        targetReceivingLotId: targetMode === 'EXISTING_LOT' ? targetReceivingLotId : undefined,
        createIdempotencyKey: idempotencyKey,
      }),
    onSuccess: (session) => {
      toast.success('Bulk import session created');
      setIdempotencyKey(generateUuid());
      navigate(ROUTES.OPS_BULK_IMPORT_DETAIL.replace(':id', session.id));
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to create import session')),
  });

  const canCreate =
    !!customerId &&
    !!customerLocationId &&
    !!complaintDescription.trim() &&
    (targetMode === 'NEW_LOT' || !!targetReceivingLotId) &&
    !createMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    createMutation.mutate();
  };

  const modeHelpText = useMemo(
    () =>
      targetMode === 'NEW_LOT'
        ? 'A new Receiving Lot will be created once this import is finalized.'
        : 'Rows will be added to the selected existing Receiving Lot once this import is finalized.',
    [targetMode],
  );

  const masterDataHint = () =>
    toast.info('Add or edit branches and contacts from the Customers page.');

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Equipment Inward', to: ROUTES.EQUIPMENT_INTAKE },
          { label: 'Bulk Import — New Session' },
        ]}
      />

      <PageHeader
        title="Bulk Import — New Session"
        description="Set up a session, download the template, then upload your completed workbook"
      />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-5">
        {/* The whole premise of the screen, stated before the first field. */}
        <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <FontAwesomeIcon icon={faTruckRampBox} className="mt-0.5 h-4 w-4 text-primary" />
          <div className="text-[13px] leading-relaxed">
            <p className="font-semibold text-foreground">One truck = one header</p>
            <p className="text-muted-foreground">
              Everything below is entered <strong>once</strong> and applies to every machine in this
              delivery. The workbook carries only what identifies each machine — serial, asset,
              model, accessories. One delivery belongs to one customer; for a mixed load, create a
              separate bulk intake.
            </p>
          </div>
        </div>

        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Customer</h2>

          <FormField label="Customer" htmlFor="bi-customer" required>
            <select
              id="bi-customer"
              value={customerId}
              onChange={(e) => handleCustomerChange(e.target.value)}
              className={SELECT_CLASS}
              required
            >
              <option value="">— Select customer —</option>
              {customers?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {formatCustomerLabel(c)}
                </option>
              ))}
            </select>
          </FormField>

          {/* Read-only, exactly as on the intake workspace — this screen must
              never become a second place that owns the registered address. */}
          {customer && <CustomerSummary customer={customer} />}
        </section>

        {customerId && (
          <section className="space-y-4 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">Delivery Header</h2>

            <FormField label="Reason for Receipt" htmlFor="bi-reason" required>
              <input
                id="bi-reason"
                type="text"
                value={complaintDescription}
                onChange={(e) => setComplaintDescription(e.target.value)}
                className={INPUT_CLASS}
                placeholder="e.g. Repair"
                maxLength={500}
                required
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Why this delivery arrived — Repair, Calibration, AMC, Breakdown, Warranty claim.
                Applied to every machine in the load.
              </p>
            </FormField>

            <AddressOverview
              customer={customer ?? null}
              locations={activeLocations}
              contacts={activeContacts}
              locationId={customerLocationId}
              contactId={customerContactId}
              billTo={billTo}
              shipTo={shipTo}
              onPickLocation={() => setPicking('location')}
              onAddLocation={masterDataHint}
              onEditLocation={masterDataHint}
              onPickContact={() => setPicking('contact')}
              onAddContact={() => setAddContactOpen(true)}
              onEditContact={masterDataHint}
              onPickReturn={() => setPicking('return')}
              onPickInvoice={() => setPicking('invoice')}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Priority" htmlFor="bi-priority">
                <select
                  id="bi-priority"
                  value={priorityId}
                  onChange={(e) => setPriorityId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">— Use default —</option>
                  {priorities?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Notes" htmlFor="bi-notes">
                <input
                  id="bi-notes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Batch note carried onto every machine"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Optional — and never a substitute for Reason for Receipt.
                </p>
              </FormField>
            </div>
          </section>
        )}

        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Receiving Lot</h2>

          <FormField label="Target" htmlFor="bi-mode" required>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="bi-mode"
                  value="NEW_LOT"
                  checked={targetMode === 'NEW_LOT'}
                  onChange={() => handleModeChange('NEW_LOT')}
                />
                Create a new Receiving Lot
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="bi-mode"
                  value="EXISTING_LOT"
                  checked={targetMode === 'EXISTING_LOT'}
                  onChange={() => handleModeChange('EXISTING_LOT')}
                />
                Add to an existing Receiving Lot
              </label>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">{modeHelpText}</p>
          </FormField>

          {targetMode === 'EXISTING_LOT' && (
            <FormField label="Target Receiving Lot" htmlFor="bi-target-lot" required>
              <select
                id="bi-target-lot"
                value={targetReceivingLotId}
                onChange={(e) => setTargetReceivingLotId(e.target.value)}
                className={SELECT_CLASS}
                disabled={!customerId || openLotsLoading}
                required
              >
                <option value="">
                  {!customerId
                    ? '— Select a customer first —'
                    : openLotsLoading
                      ? 'Loading open lots…'
                      : '— Select an open receiving lot —'}
                </option>
                {openLots?.data.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.lotNumber} — {lot.equipmentCount} item(s)
                  </option>
                ))}
              </select>
              {customerId && !openLotsLoading && openLots?.data.length === 0 && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  This customer has no open Receiving Lots. Choose "Create a new Receiving Lot"
                  instead.
                </p>
              )}
            </FormField>
          )}
        </section>

        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            disabled={!canDownloadTemplate || downloadMutation.isPending}
            onClick={() => downloadMutation.mutate()}
          >
            {downloadMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faDownload} className="mr-2 h-3.5 w-3.5" />
            )}
            Download Template
          </Button>

          <Button type="submit" disabled={!canCreate}>
            {createMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              'Create Session & Continue'
            )}
          </Button>
        </div>
      </form>

      {picking && (
        <AddressPickModal
          target={picking}
          customer={customer ?? null}
          locations={activeLocations}
          contacts={activeContacts}
          locationId={customerLocationId}
          contactId={customerContactId}
          billTo={billTo}
          shipTo={shipTo}
          onLocationChange={handleLocationChange}
          onContactChange={setCustomerContactId}
          onReturnChange={handleShipToChange}
          onInvoiceChange={setBillTo}
          onAddLocation={masterDataHint}
          onAddContact={() => setAddContactOpen(true)}
          onEditContact={masterDataHint}
          onClose={() => setPicking(null)}
        />
      )}

      <AddContactModal
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        customerId={customerId}
        locationId={customerLocationId}
        onCreated={(contact) => {
          setCustomerContactId(contact.id);
          setAddContactOpen(false);
          void refetchContacts();
        }}
      />
    </div>
  );
}
